import React from "react";

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("en-IN", { maximumFractionDigits: 2 })
    : value;
};

const formatMoney = (value) =>
  `₹${Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatExpiry = (value) => {
  if (!value) return "";
  const match = String(value).toUpperCase().match(/^(\d{2})([A-Z]{3})(\d{4})$/);
  if (!match) return value;
  const [, day, month, year] = match;
  return `${day}-${month[0]}${month.slice(1).toLowerCase()}-${year.slice(-2)}`;
};

const orderSymbol = (order) => {
  const optionType = String(order.optionType || "").toUpperCase();
  if (order.symbolName && order.expiryDate && Number(order.strikePrice) > 0
      && optionType && optionType !== "XX") {
    return `${order.symbolName} ${formatExpiry(order.expiryDate)} ${formatNumber(order.strikePrice)} ${optionType}`;
  }
  return order.symbol || "--";
};

const shortProduct = (productType) => {
  const normalized = String(productType || "").toUpperCase();
  if (normalized === "CARRYFORWARD") return "CF";
  if (normalized === "INTRADAY") return "MIS";
  if (normalized === "DELIVERY") return "CNC";
  return productType || "--";
};

const displayExchange = (exchange) => {
  const normalized = String(exchange || "").toUpperCase();
  if (normalized === "NFO") return "NSE FO";
  if (normalized === "BFO") return "BSE FO";
  if (normalized === "NSE_EQ") return "NSE";
  if (normalized === "BSE_EQ") return "BSE";
  return exchange || "--";
};

const orderLots = (order) => {
  const lotSize = Number(order.lotSize);
  if (!Number.isFinite(lotSize) || lotSize <= 0) return null;
  const totalLots = Number(order.quantity || 0) / lotSize;
  const filledLots = Number(order.filledQuantity || 0) / lotSize;
  return {
    value: `${formatNumber(filledLots)} / ${formatNumber(totalLots)} ${totalLots === 1 ? "Lot" : "Lots"}`,
    detail: `1 Lot = ${formatNumber(lotSize)}`,
  };
};

const positivePrice = (...values) => values.find(
  (value) => Number.isFinite(Number(value)) && Number(value) > 0
);

const orderPrices = (order, category) => {
  const orderType = String(order.orderType || "").toUpperCase();
  const isPending = category === "PENDING";
  const isStopLoss = orderType.includes("STOP") || orderType.includes("SL");

  if (isPending) {
    const targetValue = positivePrice(
      order.targetPrice,
      order.targetprice,
      order.target_price,
      order.target,
      order.takeProfit,
      order.squareoff,
      order.squareOff,
      order.boProfitValue,
      isStopLoss ? undefined : order.price,
      isStopLoss ? undefined : order.limitPrice,
      isStopLoss ? undefined : order.triggerPrice,
      isStopLoss ? undefined : order.trigger_price
    );
    const stopLossValue = positivePrice(
      order.stopLossPrice,
      order.stop_loss_price,
      order.stopLoss,
      order.stoploss,
      order.sl,
      order.boStopLossValue,
      isStopLoss ? order.triggerPrice : undefined,
      isStopLoss ? order.trigger_price : undefined,
      isStopLoss ? order.stopPrice : undefined
    );
    const prices = [];
    prices.push({
      label: "TG",
      value: targetValue === undefined ? "--" : formatMoney(targetValue),
      detail: "",
    });
    if (stopLossValue !== undefined) {
      prices.push({
        label: "SL",
        value: formatMoney(stopLossValue),
        detail: isStopLoss && Number(order.triggerPrice) > 0 && Number(order.price) > 0
          && Number(order.triggerPrice) !== Number(order.price)
          ? `Limit ${formatMoney(order.price)}`
          : "",
      });
    }
    return prices.length ? prices : [{ label: "", value: "--", detail: "" }];
  }

  const value = positivePrice(order.averagePrice, order.price, order.triggerPrice);
  return [{
    label: value === undefined ? "" : "AVG",
    value: value === undefined ? "--" : formatMoney(value),
    detail: "",
  }];
};

const statusClassName = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (["COMPLETE", "COMPLETED", "EXECUTED", "FILLED", "TRADED", "TRADE_EXECUTED"].includes(normalized)) return "algo-badge--active";
  if (["OPEN", "PENDING", "TRANSIT", "TRIGGER PENDING"].includes(normalized)) return "algo-badge--paused";
  if (["REJECTED", "CANCELLED", "CANCELED", "EXPIRED"].includes(normalized)) return "algo-badge--stopped";
  return "algo-badge--draft";
};

export const ORDER_CATEGORIES = ["PENDING", "EXECUTED", "REJECTED", "CANCELLED"];

export const getOrderStatusCategory = (order) => {
  if (ORDER_CATEGORIES.includes(order.statusCategory)) return order.statusCategory;
  const normalized = String(order.status || "")
    .trim()
    .toUpperCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_");
  if (normalized.includes("PENDING")
      || normalized.includes("PART_TRADED")
      || normalized.includes("PARTIALLY_FILLED")
      || ["OPEN", "TRANSIT", "MODIFIED", "TRIGGERED", "INACTIVE"].includes(normalized)
      || normalized.includes("REQ_RECEIVED")) return "PENDING";
  if (normalized.includes("REJECT") || normalized.includes("FAIL")) return "REJECTED";
  if (normalized.includes("CANCEL") || normalized.includes("EXPIRE")) return "CANCELLED";
  if (normalized.includes("COMPLETE")
      || normalized.includes("EXECUTED")
      || ["FILLED", "TRADED", "TRADE_EXECUTED"].includes(normalized)) return "EXECUTED";
  return Number(order.quantity) > 0 && Number(order.filledQuantity) >= Number(order.quantity)
    ? "EXECUTED"
    : "PENDING";
};

const BrokerOrderTable = ({ orders, isConnected, category }) => (
  <div className="algo-table-wrap">
    <table className="algo-table">
      <thead><tr><th>Order ID</th><th>Broker</th><th>Symbol</th><th>Side</th><th>Quantity</th><th>Price</th><th>LTP / Change</th><th>Status</th><th>Time</th></tr></thead>
      <tbody>
        {orders.map((order, index) => {
          const lots = orderLots(order);
          const prices = orderPrices(order, category);
          const percentChange = order.percentChange === null
            || order.percentChange === undefined
            ? null
            : Number(order.percentChange);
          return (
            <tr key={`${order.brokerAccountId}-${order.orderId || index}`}>
              <td>{order.orderId || "--"}<small>{order.exchangeOrderId || ""}</small></td>
              <td>{order.brokerAccountName || order.brokerType || "--"}<small>{order.brokerType || ""}</small></td>
              <td>{orderSymbol(order)}<small>{order.orderType || ""}</small></td>
              <td className={String(order.side).toUpperCase() === "BUY" ? "algo-positive" : "algo-negative"}>{order.side || "--"}<small>{shortProduct(order.productType)} · {displayExchange(order.exchange)}</small></td>
              <td>{lots ? lots.value : `${formatNumber(order.filledQuantity)} / ${formatNumber(order.quantity)}`}<small>{lots ? lots.detail : `${formatNumber(order.pendingQuantity)} pending`}</small></td>
              <td><div className="algo-order-price-stack">{prices.map((price) => (
                <div key={`${price.label}-${price.value}`}>
                  {price.label && <span className={`algo-order-price-label algo-order-price-label--${price.label.toLowerCase()}`}>{price.label}</span>} {price.value}
                  {price.detail && <small>{price.detail}</small>}
                </div>
              ))}</div>
              </td>
              <td>{order.lastPrice === null || order.lastPrice === undefined ? "--" : formatMoney(order.lastPrice)}
                {percentChange !== null && <small className={percentChange >= 0 ? "algo-positive" : "algo-negative"}>{percentChange >= 0 ? "+" : ""}{percentChange.toFixed(2)}%</small>}
              </td>
              <td><span className={`algo-badge ${statusClassName(order.statusCategory || order.status)}`}>{order.status || category || "UNKNOWN"}</span></td>
              <td>{order.orderTime || "--"}{order.message && <small>{order.message}</small>}</td>
            </tr>
          );
        })}
        {!orders.length && (
          <tr><td colSpan="9"><div className="algo-empty compact">
            <i className="fa fa-list-alt" /><h2>No {String(category || "broker").toLowerCase()} orders</h2>
            <p>{isConnected ? "The next broker refresh will appear automatically." : "Waiting for the secure order connection."}</p>
          </div></td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export default BrokerOrderTable;
